// Dashboard.js - Customer dashboard with tab navigation
// Tabs: My Visits, Messages, My Profile
// My Profile includes saved gravesites management
// Customers can add graves once and reuse them when booking

import React, { useEffect, useState, useRef } from 'react';
import { auth, db, storage } from '../firebase';
import { signOut, EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL
} from 'firebase/storage';
import { useNavigate, useLocation } from 'react-router-dom';
import Messages from './Messages';
import RatingModal from './RatingModal';
import NotificationBell from './NotificationBell';

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();

  // State for user profile, bookings, loading, active tab, and expanded booking
  const [userData, setUserData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [activeTab, setActiveTab] = useState('visits');
  const [ratingBooking, setRatingBooking] = useState(null);
  const [cancellingBooking, setCancellingBooking] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [cancelSuccess, setCancelSuccess] = useState('');
  const [uploadingCustomerPhoto, setUploadingCustomerPhoto] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  // Feature 3 — edit profile
  const [editingProfile, setEditingProfile] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [marketingConsentSaving, setMarketingConsentSaving] = useState(false);

  // Subscription upsell popup state
  const [showUpsell, setShowUpsell] = useState(false);

  // Change password modal state
  const [showChangePw, setShowChangePw] = useState(false);
  const [changePwForm, setChangePwForm] = useState({ current: '', next: '', confirm: '' });
  const [changePwError, setChangePwError] = useState('');
  const [changePwSuccess, setChangePwSuccess] = useState(false);
  const [changePwLoading, setChangePwLoading] = useState(false);

  // Saved gravesites state
  const [gravesites, setGravesites] = useState([]);
  const [showAddGravesite, setShowAddGravesite] = useState(false);
  const [newGravesite, setNewGravesite] = useState({
    deceasedName: '',
    cemeteryName: '',
    cemeteryCity: '',
    cemeteryState: '',
    plotNumber: '',
    notes: '',
    placeId: '',
  });
  const [savingGravesite, setSavingGravesite] = useState(false);
  const [gravesiteError, setGravesiteError] = useState('');

  // Refs for the gravesite form cemetery autocomplete
  const gravesiteCemeteryInputRef = useRef(null);
  const gravesiteAutocompleteRef = useRef(null);

  useEffect(() => {
    // onAuthStateChanged waits for Firebase to restore session on refresh
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        // Fetch customer profile from Firestore
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();

          // Gate 1 — onboarding happens during signup, not on a separate page.
          // If not complete just show dashboard; the onboarding checklist widget
          // inside the dashboard will guide the customer through remaining steps.
          if (!data.onboardingComplete) {
            // Don't redirect — show dashboard with onboarding checklist visible
            // setOnboardingDismissed(false) below ensures the checklist shows
          }

          // Gate 2 — terms must be acknowledged after onboarding
          if (!data.termsAcknowledged) {
            navigate('/terms-acknowledge');
            return;
          }

          setUserData(data);
          setEditFirstName(data.firstName || '');
          setEditLastName(data.lastName || '');
          setOnboardingDismissed(data.onboardingDismissed || false);
          setMarketingConsent(data.marketingConsent || false);
        }

        // Fetch all bookings for this customer
        const bookingsQuery = query(
          collection(db, 'bookings'),
          where('customerId', '==', user.uid)
        );

        const bookingsSnapshot = await getDocs(bookingsQuery);
        const bookingsList = bookingsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Sort newest first
        bookingsList.sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return dateB - dateA;
        });

        setBookings(bookingsList);

        // Fetch saved gravesites for this customer
        // Gravesites are stored in a subcollection under the user's document
        const gravesitesQuery = query(
          collection(db, 'gravesites'),
          where('customerId', '==', user.uid)
        );

        const gravesitesSnapshot = await getDocs(gravesitesQuery);
        const gravesitesList = gravesitesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setGravesites(gravesitesList);

      } catch (err) {
        console.error('Error fetching data:', err);
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for unread messages across all conversations
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', user.uid)
    );

    const unsubscribe = onSnapshot(q, async (convSnap) => {
      let total = 0;
      for (const convDoc of convSnap.docs) {
        const msgsSnap = await getDocs(
          query(
            collection(db, 'conversations', convDoc.id, 'messages'),
            where('senderId', '!=', user.uid)
          )
        );
        const unread = msgsSnap.docs.filter(msgDoc => {
          const data = msgDoc.data();
          return !(data.readBy || []).includes(user.uid);
        }).length;
        total += unread;
      }
      setUnreadCount(total);
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load Google Places autocomplete on the gravesite form cemetery input.
  // Only initializes when the form is open to avoid loading the script on every page load.
  useEffect(() => {
    if (!showAddGravesite) return;

    const initAutocomplete = () => {
      if (!gravesiteCemeteryInputRef.current || !window.google) return;

      gravesiteAutocompleteRef.current = new window.google.maps.places.Autocomplete(
        gravesiteCemeteryInputRef.current,
        {
          types: ['establishment'],
          componentRestrictions: { country: 'us' },
          fields: ['name', 'address_components', 'place_id'],
        }
      );

      gravesiteAutocompleteRef.current.addListener('place_changed', () => {
        const place = gravesiteAutocompleteRef.current.getPlace();
        if (!place || !place.address_components) return;

        let city = '';
        let state = '';
        place.address_components.forEach(component => {
          if (component.types.includes('locality')) city = component.long_name;
          if (component.types.includes('administrative_area_level_1')) state = component.short_name;
        });

        setNewGravesite(prev => ({
          ...prev,
          cemeteryName: place.name || '',
          cemeteryCity: city,
          cemeteryState: state.toLowerCase(),
          placeId: place.place_id || '',
        }));

        if (gravesiteCemeteryInputRef.current) {
          gravesiteCemeteryInputRef.current.value = place.name || '';
        }
      });
    };

    if (window.google && window.google.maps && window.google.maps.places) {
      initAutocomplete();
      return;
    }

    const existingScript = document.getElementById('google-places-script');
    if (existingScript) {
      existingScript.onload = initAutocomplete;
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-places-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_PLACES_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = initAutocomplete;
    document.head.appendChild(script);

    return () => {
      if (gravesiteAutocompleteRef.current && window.google) {
        window.google.maps.event.clearInstanceListeners(gravesiteAutocompleteRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddGravesite]);

  // Subscription upsell: show popup when customer arrives from a completed booking
  // Conditions: ?booked=true in URL, not shown this session, no active subscription
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromBooking = params.get('booked') === 'true';
    const alreadyShown = sessionStorage.getItem('upsellShown');
    if (!fromBooking || alreadyShown) return;

    const checkAndShow = async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists() && snap.data().subscriptionStatus === 'active') return;
      } catch {
        // Fail open — if we can't check, still show the popup
      }
      const timer = setTimeout(() => {
        setShowUpsell(true);
      }, 2000);
      return () => clearTimeout(timer);
    };

    checkAndShow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissUpsell = () => {
    setShowUpsell(false);
    sessionStorage.setItem('upsellShown', 'true');
  };

  const handleLogout = async () => {
    await signOut(auth);
    localStorage.clear();
    navigate('/');
  };

  // Toggles photo section open and closed; marks photos as viewed in localStorage
  const toggleExpanded = (bookingId) => {
    setExpandedBooking(expandedBooking === bookingId ? null : bookingId);
    if (expandedBooking !== bookingId) {
      localStorage.setItem(`gravemark_viewed_photos_${bookingId}`, 'true');
    }
  };

  // Handles click on photo-ready banner — marks viewed, expands card, scrolls to it
  const handlePhotoBannerClick = (booking) => {
    localStorage.setItem(`gravemark_viewed_photos_${booking.id}`, 'true');
    setExpandedBooking(booking.id);
    setTimeout(() => {
      document.getElementById(`booking-card-${booking.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  // Dismisses the onboarding checklist and persists the flag to Firestore
  const handleDismissOnboarding = async () => {
    const user = auth.currentUser;
    if (!user) return;
    await updateDoc(doc(db, 'users', user.uid), { onboardingDismissed: true });
    setOnboardingDismissed(true);
  };

  // Opens or creates a conversation between customer and caretaker
  const handleStartConversation = async (booking) => {
    const user = auth.currentUser;
    if (!user || !booking.providerId) return;

    try {
      const existingQuery = query(
        collection(db, 'conversations'),
        where('bookingId', '==', booking.id)
      );

      const existingSnapshot = await getDocs(existingQuery);

      if (existingSnapshot.empty) {
        await addDoc(collection(db, 'conversations'), {
          bookingId: booking.id,
          participants: [user.uid, booking.providerId],
          customerId: user.uid,
          customerName: `${userData?.firstName} ${userData?.lastName}`,
          providerId: booking.providerId,
          providerName: booking.providerName,
          lovedOneName: booking.lovedOneName,
          lastMessage: '',
          lastMessageAt: serverTimestamp(),
          createdAt: serverTimestamp(),
        });
      }

      setActiveTab('messages');

    } catch (err) {
      console.error('Error starting conversation:', err);
    }
  };

  // Confirms cancellation — calls backend refund endpoint, then updates local state
  // Takes bookingId and bookingStatus explicitly so there's no stale closure risk
  const handleConfirmCancel = async (bookingId, bookingStatus) => {
    // Guard: assigned/completed bookings cannot be self-cancelled
    if (bookingStatus === 'assigned' || bookingStatus === 'completed') {
      setCancelError('This booking cannot be cancelled because a caretaker has already been assigned. Please contact support@evermarkmemorial.com for assistance.');
      setCancellingBooking(null);
      return;
    }

    setCancelLoading(true);
    setCancelError('');

    try {
      // Get a fresh Firebase ID token to authenticate the backend request
      const token = await auth.currentUser.getIdToken();

      // Call the backend refund endpoint — it verifies ownership, checks eligibility,
      // issues the Square refund, and updates Firestore atomically
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/refund-payment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ bookingId }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Refund failed');
      }

      // Optimistically update local state so the card flips to cancelled immediately
      setBookings(prev => prev.map(b =>
        b.id === bookingId ? { ...b, status: 'cancelled', refunded: true } : b
      ));

      setCancellingBooking(null);
      setCancelSuccess('Your booking has been cancelled and a full refund has been issued. Please allow 5–10 business days for the refund to appear on your statement.');

    } catch (err) {
      console.error('Cancellation error:', err);
      setCancelError(err.message || 'Cancellation failed. Please contact support@evermarkmemorial.com for assistance.');
      setCancellingBooking(null);
    } finally {
      setCancelLoading(false);
    }
  };

  // Called when rating is submitted successfully
  const handleRatingSubmitted = () => {
    setBookings(prev => prev.map(b =>
      b.id === ratingBooking?.id ? { ...b, rated: true } : b
    ));
    setRatingBooking(null);
  };

  // Called when customer uploads a profile photo
  const handleCustomerPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (JPG or PNG).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('Photo must be under 10 MB.');
      return;
    }

    const user = auth.currentUser;
    if (!user) return;

    try {
      setUploadingCustomerPhoto(true);

      const photoRef = ref(storage, `profile-photos/${user.uid}`);
      await uploadBytes(photoRef, file);
      const downloadURL = await getDownloadURL(photoRef);

      await updateDoc(doc(db, 'users', user.uid), {
        photoUrl: downloadURL
      });

      setUserData(prev => ({ ...prev, photoUrl: downloadURL }));

    } catch (err) {
      console.error('Error uploading customer photo:', err);
    } finally {
      setUploadingCustomerPhoto(false);
    }
  };

  // Password strength checks (used by change-password modal)
  const pwChecks = (pw) => ({
    length: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    number: /[0-9]/.test(pw),
    special: /[!@#$%^&*()\-_=+[\]{}|;:,.<>?]/.test(pw),
  });

  // Re-authenticates then sets a new password
  const handleChangePassword = async () => {
    setChangePwError('');
    const { current, next, confirm } = changePwForm;
    if (!current || !next || !confirm) { setChangePwError('All fields are required.'); return; }
    if (next !== confirm) { setChangePwError('New passwords do not match.'); return; }
    if (!Object.values(pwChecks(next)).every(Boolean)) {
      setChangePwError('New password does not meet strength requirements.'); return;
    }
    setChangePwLoading(true);
    try {
      const credential = EmailAuthProvider.credential(auth.currentUser.email, current);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, next);
      setChangePwSuccess(true);
      setChangePwForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setChangePwError('Current password is incorrect.');
      } else {
        setChangePwError('Failed to update password. Please try again.');
      }
    }
    setChangePwLoading(false);
  };

  // Saves updated first/last name to Firestore
  const handleSaveProfile = async () => {
    const user = auth.currentUser;
    if (!user) return;
    if (!editFirstName.trim() || !editLastName.trim()) {
      setProfileError('Name fields cannot be empty.');
      return;
    }
    try {
      setProfileSaving(true);
      setProfileError('');
      await updateDoc(doc(db, 'users', user.uid), {
        firstName: editFirstName.trim(),
        lastName: editLastName.trim(),
      });
      setUserData(prev => ({ ...prev, firstName: editFirstName.trim(), lastName: editLastName.trim() }));
      setEditingProfile(false);
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err) {
      setProfileError('Something went wrong. Please try again.');
    } finally {
      setProfileSaving(false);
    }
  };

  // Toggles marketing consent preference and saves immediately to Firestore
  const handleMarketingConsentToggle = async () => {
    const user = auth.currentUser;
    if (!user || marketingConsentSaving) return;
    const newValue = !marketingConsent;
    setMarketingConsent(newValue);
    setMarketingConsentSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), { marketingConsent: newValue });
    } catch (err) {
      console.error('Failed to update marketing consent:', err);
      setMarketingConsent(!newValue); // revert on failure
    } finally {
      setMarketingConsentSaving(false);
    }
  };

  // Saves a new gravesite to Firestore
  const handleSaveGravesite = async () => {
    const user = auth.currentUser;
    if (!user) return;

    // Read cemetery name from the autocomplete input ref (uncontrolled) or state fallback
    const finalCemeteryName = gravesiteCemeteryInputRef.current?.value?.trim() || newGravesite.cemeteryName;

    // Validate required fields
    if (!newGravesite.deceasedName || !finalCemeteryName ||
      !newGravesite.cemeteryCity || !newGravesite.cemeteryState) {
      setGravesiteError('Please fill in all required fields');
      return;
    }

    try {
      setSavingGravesite(true);
      setGravesiteError('');

      const gravesiteRef = await addDoc(collection(db, 'gravesites'), {
        customerId: user.uid,
        deceasedName: newGravesite.deceasedName,
        lovedOneName: newGravesite.deceasedName,   // kept for booking autofill compatibility
        cemeteryName: finalCemeteryName,
        city: newGravesite.cemeteryCity.toLowerCase().trim(),
        cemeteryCity: newGravesite.cemeteryCity.toLowerCase().trim(),
        state: newGravesite.cemeteryState.toLowerCase().trim(),
        cemeteryState: newGravesite.cemeteryState.toLowerCase().trim(),
        plotNumber: newGravesite.plotNumber || '',
        placeId: newGravesite.placeId || '',
        notes: newGravesite.notes || '',
        createdAt: new Date(),
      });

      // Add to local state so it shows immediately
      setGravesites(prev => [...prev, {
        id: gravesiteRef.id,
        customerId: user.uid,
        deceasedName: newGravesite.deceasedName,
        lovedOneName: newGravesite.deceasedName,
        cemeteryName: finalCemeteryName,
        cemeteryCity: newGravesite.cemeteryCity.toLowerCase().trim(),
        cemeteryState: newGravesite.cemeteryState.toLowerCase().trim(),
        plotNumber: newGravesite.plotNumber || '',
        placeId: newGravesite.placeId || '',
        notes: newGravesite.notes || '',
      }]);

      // Reset form and hide it
      const emptyGravesite = { deceasedName: '', cemeteryName: '', cemeteryCity: '', cemeteryState: '', plotNumber: '', notes: '', placeId: '' };
      setNewGravesite(emptyGravesite);
      if (gravesiteCemeteryInputRef.current) gravesiteCemeteryInputRef.current.value = '';
      setShowAddGravesite(false);

    } catch (err) {
      console.error('Error saving gravesite:', err);
      setGravesiteError('Something went wrong. Please try again.');
    } finally {
      setSavingGravesite(false);
    }
  };

  // Deletes a saved gravesite
  const handleDeleteGravesite = async (gravesiteId) => {
    try {
      await deleteDoc(doc(db, 'gravesites', gravesiteId));
      setGravesites(prev => prev.filter(g => g.id !== gravesiteId));
    } catch (err) {
      console.error('Error deleting gravesite:', err);
    }
  };

  // ========== COMPUTED STATS ==========
  const totalBookings = bookings.filter(b => b.status !== 'pending_payment').length;
  const completedVisits = bookings.filter(b => b.status === 'completed').length;
  const pendingVisits = bookings.filter(b =>
    b.status === 'pending' || b.status === 'assigned'
  ).length;

  // Onboarding checklist steps
  const hasViewedAnyPhotos = bookings.some(b =>
    b.status === 'completed' &&
    (b.beforePhotoUrl || b.afterPhotoUrl) &&
    localStorage.getItem(`gravemark_viewed_photos_${b.id}`)
  );
  const onboardingSteps = [
    { label: 'Add your first gravesite', done: gravesites.length > 0 },
    { label: 'Book your first visit', done: totalBookings > 0 },
    { label: 'View your photo report', done: hasViewedAnyPhotos },
    { label: 'Rate your caretaker', done: bookings.some(b => b.rated) },
  ];
  const allOnboardingDone = onboardingSteps.every(s => s.done);

  // First completed booking with photos not yet viewed — drives the photo-ready banner
  const photoReadyBooking = bookings.find(b =>
    b.status === 'completed' &&
    (b.beforePhotoUrl || b.afterPhotoUrl) &&
    !localStorage.getItem(`gravemark_viewed_photos_${b.id}`)
  ) || null;

  // Helper to format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC'
    });
  };

  // Helper to capitalize words
  const capitalize = (str) => {
    if (!str) return '';
    return str.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard-header" style={{ padding: '32px 40px 0' }}>
          <div className="skeleton-line skeleton-line-medium" style={{ height: '28px', marginBottom: '8px' }} />
          <div className="skeleton-line skeleton-line-short" style={{ height: '16px' }} />
        </div>
        <div className="skeleton-stats-bar">
          {[1, 2, 3].map(i => <div key={i} className="skeleton-stat" />)}
        </div>
        <div className="skeleton-tabs">
          {[1, 2, 3].map(i => <div key={i} className="skeleton-tab" />)}
        </div>
        <div className="skeleton-cards-wrapper">
          {[1, 2].map(i => (
            <div key={i} className="skeleton-card">
              <div style={{ flex: 1 }}>
                <div className="skeleton-line skeleton-line-short" style={{ height: '20px', marginBottom: '12px' }} />
                <div className="skeleton-line skeleton-line-medium" style={{ height: '14px', marginBottom: '8px' }} />
                <div className="skeleton-line skeleton-line-short" style={{ height: '14px' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                <div className="skeleton-line" style={{ width: '80px', height: '24px', borderRadius: '50px' }} />
                <div className="skeleton-line" style={{ width: '100px', height: '32px', borderRadius: '50px' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="dashboard">
        <div className="fetch-error-state">
          <div className="fetch-error-icon">⚠️</div>
          <p>Something went wrong. Please refresh and try again.</p>
          <button className="btn-primary" onClick={() => window.location.reload()}>Refresh</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">

      {/* ========== NAVIGATION BAR ========== */}
      <nav className="dashboard-nav">
        <a href="/" className="logo" style={{ textDecoration: 'none' }}>Evermark</a>
        <div className="dashboard-nav-right">
          {userData && (
            <span className="dashboard-greeting">
              Hello, {userData.firstName}
            </span>
          )}
          <NotificationBell />
          <button className="btn-outline" onClick={handleLogout}>
            Log Out
          </button>
        </div>
      </nav>

      {/* ========== TAB NAVIGATION ========== */}
      <div className="dashboard-tabs">
        <button
          className={`dashboard-tab ${activeTab === 'visits' ? 'dashboard-tab-active' : ''}`}
          onClick={() => setActiveTab('visits')}
        >
          🌿 My Visits
        </button>
        <button
          className={`dashboard-tab ${activeTab === 'messages' ? 'dashboard-tab-active' : ''}`}
          onClick={() => { setActiveTab('messages'); setUnreadCount(0); }}
        >
          💬 Messages {unreadCount > 0 && <span className="tab-badge">{unreadCount}</span>}
        </button>
        <button
          className={`dashboard-tab ${activeTab === 'profile' ? 'dashboard-tab-active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          👤 My Profile
        </button>
      </div>

      <div className="dashboard-content">

        {/* ========== MY VISITS TAB ========== */}
        {activeTab === 'visits' && (
          <div>
            <div className="dashboard-header">
              <div>
                <h2>My Visits</h2>
                <p className="dashboard-subtitle">
                  Manage your grave visits and view photo reports
                </p>
              </div>
              <button className="btn-primary" onClick={() => navigate('/book')}>
                + Book a Visit
              </button>
            </div>

            {/* Onboarding checklist — shown until dismissed or all steps done */}
            {!onboardingDismissed && (
              <div className="onboarding-checklist">
                <div className="onboarding-checklist-header">
                  <h3>Getting Started</h3>
                  <button className="btn-close-onboarding" onClick={handleDismissOnboarding}>✕</button>
                </div>
                {allOnboardingDone ? (
                  <div className="onboarding-complete">
                    <span className="onboarding-complete-icon">🎉</span>
                    <p>You're all set! You've got the most out of Evermark Memorial.</p>
                    <button className="btn-edit-inline" onClick={handleDismissOnboarding}>Dismiss</button>
                  </div>
                ) : (
                  <>
                    <p className="onboarding-subtitle">Complete these steps to get started</p>
                    <div className="onboarding-steps">
                      {onboardingSteps.map((step, i) => (
                        <div key={i} className={`onboarding-step ${step.done ? 'onboarding-step-done' : ''}`}>
                          <span className="onboarding-step-check">{step.done ? '✅' : '○'}</span>
                          <span className="onboarding-step-label">{step.label}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Photo-ready banner — shown when a completed booking has unviewed photos */}
            {photoReadyBooking && (
              <div
                className="photo-ready-banner"
                onClick={() => handlePhotoBannerClick(photoReadyBooking)}
              >
                <span className="photo-ready-icon">📸</span>
                <span>Your photos are ready for <strong>{photoReadyBooking.lovedOneName}</strong> — tap to view</span>
              </div>
            )}

            {/* Stats bar */}
            <div className="stats-bar">
              <div className="stat-card">
                <div className="stat-icon">📋</div>
                <div className="stat-info">
                  <span className="stat-number">{totalBookings}</span>
                  <span className="stat-label">Total Visits</span>
                </div>
              </div>
              <div className="stat-card stat-card-green">
                <div className="stat-icon">✅</div>
                <div className="stat-info">
                  <span className="stat-number">{completedVisits}</span>
                  <span className="stat-label">Completed</span>
                </div>
              </div>
              <div className="stat-card stat-card-yellow">
                <div className="stat-icon">⏳</div>
                <div className="stat-info">
                  <span className="stat-number">{pendingVisits}</span>
                  <span className="stat-label">In Progress</span>
                </div>
              </div>
              <div className="stat-card stat-card-message">
                <div className="stat-message-text">
                  {completedVisits > 0
                    ? `${completedVisits} visit${completedVisits > 1 ? 's' : ''} completed — thank you for remembering.`
                    : totalBookings > 0
                      ? 'Your caretaker is being matched — we\'ll notify you soon.'
                      : 'Book your first visit and honor their memory.'}
                </div>
              </div>
            </div>

            {/* Bookings list */}
            {bookings.filter(b => b.status !== 'pending_payment').length > 0 ? (
              <div className="bookings-list">
                {bookings
                  .filter(b => b.status !== 'pending_payment')
                  .map(booking => (
                    <div key={booking.id} id={`booking-card-${booking.id}`} className="booking-card-wrapper">
                      <div className="booking-card">
                        <div className="booking-card-left">
                          <span className="booking-badge">Grave Visit</span>
                          <h3>{booking.lovedOneName}</h3>
                          <p>{capitalize(booking.cemeteryName)}</p>
                          <p>{capitalize(booking.cemeteryCity)}, {booking.cemeteryState.toUpperCase()}</p>
                          <p className="booking-date">Requested: {formatDate(booking.preferredDate)}</p>
                          {booking.graveSection && (
                            <p className="booking-date">Section/Row: {booking.graveSection}</p>
                          )}
                          {/* Add-on tags — shown if customer selected any extras */}
                          {booking.addOns?.length > 0 && (
                            <div className="booking-card-addons">
                              {booking.addOns.map(a => (
                                <span key={a.id} className="payment-addon-tag">{a.label}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="booking-card-right">
                          <span className={`status-badge status-${booking.status}`}>
                            {booking.status === 'assigned' ? 'Caretaker Assigned'
                              : booking.status === 'cancelled' ? 'Cancelled'
                              : booking.status}
                          </span>

                          {/* Cancel button — only on pending or assigned bookings */}
                          {(booking.status === 'pending' || booking.status === 'assigned') && (
                            <button
                              className="btn-cancel-booking"
                              onClick={() => setCancellingBooking(booking)}
                            >
                              Cancel Booking
                            </button>
                          )}

                          {/* View Photos button */}
                          {booking.status === 'completed' &&
                            (booking.beforePhotoUrl || booking.afterPhotoUrl) && (
                              <button
                                className="btn-photos"
                                onClick={() => toggleExpanded(booking.id)}
                              >
                                {expandedBooking === booking.id ? 'Hide Photos ▲' : '📷 View Photos ▼'}
                              </button>
                            )}

                          {/* Rate button */}
                          {booking.status === 'completed' && !booking.rated && booking.providerId && (
                            <button
                              className="btn-rate"
                              onClick={() => setRatingBooking(booking)}
                            >
                              ⭐ Rate Caretaker
                            </button>
                          )}

                          {/* Shows submitted rating */}
                          {booking.rated && booking.customerRating && (
                            <div className="submitted-rating">
                              <span style={{ color: '#f59e0b' }}>
                                {'★'.repeat(booking.customerRating)}
                                {'☆'.repeat(5 - booking.customerRating)}
                              </span>
                              <span style={{ fontSize: '12px', color: '#888', marginLeft: '4px' }}>
                                Your rating
                              </span>
                            </div>
                          )}

                          {/* Book Again — pre-fills booking form with same details */}
                          {booking.status === 'completed' && (
                            <button
                              className="btn-rebook"
                              onClick={() => navigate('/book', {
                                state: {
                                  gravesite: {
                                    lovedOneName: booking.lovedOneName,
                                    cemeteryName: booking.cemeteryName,
                                    cemeteryCity: booking.cemeteryCity,
                                    cemeteryState: booking.cemeteryState,
                                    notes: booking.specialInstructions || ''
                                  }
                                }
                              })}
                            >
                              🔁 Book Again
                            </button>
                          )}

                          {/* Caretaker photo, name, rating, and bio snippet */}
                          {booking.providerName && (
                            <div className="caretaker-info-wrapper">
                              <div className="caretaker-info">
                                {booking.providerPhotoUrl ? (
                                  <img
                                    src={booking.providerPhotoUrl}
                                    alt={booking.providerName}
                                    className="caretaker-photo"
                                  />
                                ) : (
                                  <div className="caretaker-avatar">
                                    {booking.providerName.charAt(0)}
                                  </div>
                                )}
                                <div className="caretaker-details">
                                  <span className="provider-name">
                                    {booking.providerName}
                                  </span>
                                  {booking.providerRating > 0 && (
                                    <span className="provider-rating-mini">
                                      ⭐ {booking.providerRating.toFixed(1)}
                                    </span>
                                  )}
                                  {booking.providerBio && (
                                    <p className="provider-bio-snippet">
                                      {booking.providerBio.length > 80
                                        ? booking.providerBio.slice(0, 80) + '…'
                                        : booking.providerBio}
                                    </p>
                                  )}
                                </div>
                              </div>
                              {booking.status === 'assigned' && (
                                <button
                                  className="btn-message"
                                  onClick={() => handleStartConversation(booking)}
                                >
                                  💬 Message
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Status timeline — shown on assigned and completed bookings */}
                      {booking.status !== 'pending' && (
                        <div className="booking-timeline">
                          {[
                            { key: 'pending', label: 'Booked' },
                            { key: 'assigned', label: 'Caretaker Assigned' },
                            { key: 'completed', label: 'Visit Complete' }
                          ].map((step, i, arr) => {
                            const statusOrder = ['pending', 'assigned', 'completed'];
                            const currentIdx = statusOrder.indexOf(booking.status);
                            const stepIdx = statusOrder.indexOf(step.key);
                            const isComplete = stepIdx <= currentIdx;
                            return (
                              <React.Fragment key={step.key}>
                                <div className="timeline-step">
                                  <div className={`timeline-dot ${isComplete ? 'timeline-dot-active' : ''}`} />
                                  <span className={`timeline-label ${isComplete ? 'timeline-label-active' : ''}`}>
                                    {step.label}
                                  </span>
                                </div>
                                {i < arr.length - 1 && (
                                  <div className={`timeline-connector ${stepIdx < currentIdx ? 'timeline-connector-active' : ''}`} />
                                )}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      )}

                      {/* Expandable photo report */}
                      {expandedBooking === booking.id && (
                        <div className="photo-report">
                          <h4>📷 Visit Photo Report</h4>
                          <p className="photo-report-subtitle">
                            Photos submitted by your caretaker
                          </p>
                          <div className="photo-grid">
                            {booking.beforePhotoUrl && (
                              <div className="photo-item">
                                <p className="photo-label">Before</p>
                                <img src={booking.beforePhotoUrl} alt="Before" className="photo-img" />
                                <a href={booking.beforePhotoUrl} target="_blank" rel="noreferrer" className="photo-download">View full size</a>
                              </div>
                            )}
                            {booking.afterPhotoUrl && (
                              <div className="photo-item">
                                <p className="photo-label">After</p>
                                <img src={booking.afterPhotoUrl} alt="After" className="photo-img" />
                                <a href={booking.afterPhotoUrl} target="_blank" rel="noreferrer" className="photo-download">View full size</a>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">🌿</div>
                <h3>You haven't booked a visit yet</h3>
                <p>Book your first visit and we'll take care of the rest.</p>
                <button className="btn-primary" onClick={() => navigate('/book')}>Book a Visit</button>
              </div>
            )}
          </div>
        )}

        {/* ========== MESSAGES TAB ========== */}
        {activeTab === 'messages' && (
          <div>
            <div className="dashboard-header">
              <div>
                <h2>Messages</h2>
                <p className="dashboard-subtitle">Chat with your caretakers</p>
              </div>
            </div>
            <Messages userRole="customer" />
          </div>
        )}

        {/* ========== MY PROFILE TAB ========== */}
        {activeTab === 'profile' && (
          <div>
            <div className="dashboard-header">
              <div>
                <h2>My Profile</h2>
                <p className="dashboard-subtitle">Manage your account and saved gravesites</p>
              </div>
            </div>

            {/* Profile card with optional photo */}
            <div className="profile-card">
              <div className="profile-photo-container">
                <label className="profile-photo-clickable">
                  {userData?.photoUrl ? (
                    <img
                      src={userData.photoUrl}
                      alt="Profile"
                      className="profile-photo"
                    />
                  ) : (
                    <div className="profile-avatar">
                      {userData?.firstName?.charAt(0)}{userData?.lastName?.charAt(0)}
                    </div>
                  )}
                  <div className="profile-photo-overlay">
                    {uploadingCustomerPhoto ? '⏳' : '📷'}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleCustomerPhotoUpload}
                    disabled={uploadingCustomerPhoto}
                  />
                </label>
                <p style={{ fontSize: '11px', color: '#aaa', marginTop: '4px' }}>
                  {uploadingCustomerPhoto ? 'Uploading...' : 'Click to add photo'}
                </p>
              </div>
              <div className="profile-info">
                <h3>{userData?.firstName} {userData?.lastName}</h3>
                <p>{userData?.email}</p>
                <span className="profile-role-badge">Customer</span>
              </div>
            </div>

            {/* Account details — First/Last Name are editable, rest are read-only */}
            <div className="profile-details">
              <div className="profile-detail-row">
                <span className="profile-detail-label">First Name</span>
                {editingProfile ? (
                  <input
                    className="profile-edit-input"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    maxLength={100}
                  />
                ) : (
                  <span className="profile-detail-value">{userData?.firstName}</span>
                )}
              </div>
              <div className="profile-detail-row">
                <span className="profile-detail-label">Last Name</span>
                {editingProfile ? (
                  <input
                    className="profile-edit-input"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    maxLength={100}
                  />
                ) : (
                  <span className="profile-detail-value">{userData?.lastName}</span>
                )}
              </div>
              <div className="profile-detail-row">
                <span className="profile-detail-label">Email</span>
                <span className="profile-detail-value">{userData?.email}</span>
              </div>
              <div className="profile-detail-row">
                <span className="profile-detail-label">Member Since</span>
                <span className="profile-detail-value">
                  {userData?.createdAt?.toDate
                    ? userData.createdAt.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
                    : 'Recently joined'}
                </span>
              </div>
              <div className="profile-detail-row">
                <span className="profile-detail-label">Total Visits</span>
                <span className="profile-detail-value">{totalBookings}</span>
              </div>

              {/* Edit Profile / Save / Cancel buttons */}
              {editingProfile ? (
                <div className="profile-save-row">
                  <button
                    className="btn-primary"
                    onClick={handleSaveProfile}
                    disabled={profileSaving}
                  >
                    {profileSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    className="btn-outline"
                    onClick={() => {
                      setEditFirstName(userData?.firstName || '');
                      setEditLastName(userData?.lastName || '');
                      setEditingProfile(false);
                      setProfileError('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="profile-save-row">
                  <button className="btn-edit-inline" onClick={() => setEditingProfile(true)}>
                    Edit Profile
                  </button>
                  <button
                    className="btn-outline"
                    style={{ fontSize: '13px' }}
                    onClick={() => { setShowChangePw(true); setChangePwSuccess(false); setChangePwError(''); }}
                  >
                    Change Password
                  </button>
                </div>
              )}
              {profileError && <p className="profile-error-msg">{profileError}</p>}
              {profileSuccess && <p className="profile-success-msg">Profile updated successfully.</p>}

              {/* ========== MARKETING CONSENT TOGGLE ========== */}
              <div className="profile-detail-row" style={{ marginTop: '20px', alignItems: 'flex-start', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', width: '100%' }}>
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={handleMarketingConsentToggle}
                    disabled={marketingConsentSaving}
                    style={{ marginTop: '3px', flexShrink: 0 }}
                  />
                  <span style={{ fontSize: '14px', color: '#555', lineHeight: '1.5' }}>
                    <strong>Photo &amp; Testimonial Consent</strong><br />
                    Allow Evermark Memorial to use my visit photos and experience for testimonials or social media
                    {marketingConsentSaving && <span style={{ color: '#4a7c59', marginLeft: '8px', fontSize: '12px' }}>Saving...</span>}
                  </span>
                </label>
              </div>
            </div>

            {/* ========== SAVED GRAVESITES SECTION ========== */}
            <div className="gravesites-section">
              <div className="gravesites-header">
                <div>
                  <h3>My Gravesites</h3>
                  <p className="gravesites-subtitle">
                    Save your loved ones' grave details for quick booking
                  </p>
                </div>
                {/* Add gravesite button — also resets form when closing */}
                <button
                  className="btn-primary"
                  onClick={() => {
                    const opening = !showAddGravesite;
                    setShowAddGravesite(opening);
                    if (!opening) {
                      setNewGravesite({ deceasedName: '', cemeteryName: '', cemeteryCity: '', cemeteryState: '', plotNumber: '', notes: '', placeId: '' });
                      if (gravesiteCemeteryInputRef.current) gravesiteCemeteryInputRef.current.value = '';
                      setGravesiteError('');
                    }
                  }}
                >
                  {showAddGravesite ? 'Cancel' : '+ Add Gravesite'}
                </button>
              </div>

              {/* Add gravesite form — shown when button clicked */}
              {showAddGravesite && (
                <div className="add-gravesite-form">
                  <h4>Add a New Gravesite</h4>

                  {gravesiteError && <div className="auth-error">{gravesiteError}</div>}

                  {/* Cemetery name — Google Places autocomplete */}
                  <div className="auth-field">
                    <label>Cemetery Name <span className="required">*</span></label>
                    <input
                      ref={gravesiteCemeteryInputRef}
                      type="text"
                      placeholder="Start typing a cemetery name..."
                      defaultValue=""
                      className="auth-input-autocomplete"
                    />
                    <p className="autocomplete-hint">
                      Select from the dropdown to auto-fill city and state
                    </p>
                  </div>

                  {/* Name on the grave */}
                  <div className="auth-field">
                    <label>Name on the Grave <span className="required">*</span></label>
                    <input
                      type="text"
                      placeholder="e.g. John Smith"
                      value={newGravesite.deceasedName}
                      onChange={(e) => setNewGravesite(prev => ({ ...prev, deceasedName: e.target.value }))}
                    />
                  </div>

                  {/* City and state */}
                  <div className="auth-row">
                    <div className="auth-field">
                      <label>City <span className="required">*</span></label>
                      <input
                        type="text"
                        placeholder="e.g. Pittsburgh"
                        value={newGravesite.cemeteryCity}
                        onChange={(e) => setNewGravesite(prev => ({ ...prev, cemeteryCity: e.target.value }))}
                      />
                    </div>
                    <div className="auth-field">
                      <label>State <span className="required">*</span></label>
                      <select
                        className="booking-select"
                        value={newGravesite.cemeteryState}
                        onChange={(e) => setNewGravesite(prev => ({ ...prev, cemeteryState: e.target.value }))}
                      >
                        <option value="">Select state</option>
                        <option value="al">Alabama</option>
                        <option value="ak">Alaska</option>
                        <option value="az">Arizona</option>
                        <option value="ar">Arkansas</option>
                        <option value="ca">California</option>
                        <option value="co">Colorado</option>
                        <option value="ct">Connecticut</option>
                        <option value="de">Delaware</option>
                        <option value="fl">Florida</option>
                        <option value="ga">Georgia</option>
                        <option value="hi">Hawaii</option>
                        <option value="id">Idaho</option>
                        <option value="il">Illinois</option>
                        <option value="in">Indiana</option>
                        <option value="ia">Iowa</option>
                        <option value="ks">Kansas</option>
                        <option value="ky">Kentucky</option>
                        <option value="la">Louisiana</option>
                        <option value="me">Maine</option>
                        <option value="md">Maryland</option>
                        <option value="ma">Massachusetts</option>
                        <option value="mi">Michigan</option>
                        <option value="mn">Minnesota</option>
                        <option value="ms">Mississippi</option>
                        <option value="mo">Missouri</option>
                        <option value="mt">Montana</option>
                        <option value="ne">Nebraska</option>
                        <option value="nv">Nevada</option>
                        <option value="nh">New Hampshire</option>
                        <option value="nj">New Jersey</option>
                        <option value="nm">New Mexico</option>
                        <option value="ny">New York</option>
                        <option value="nc">North Carolina</option>
                        <option value="nd">North Dakota</option>
                        <option value="oh">Ohio</option>
                        <option value="ok">Oklahoma</option>
                        <option value="or">Oregon</option>
                        <option value="pa">Pennsylvania</option>
                        <option value="ri">Rhode Island</option>
                        <option value="sc">South Carolina</option>
                        <option value="sd">South Dakota</option>
                        <option value="tn">Tennessee</option>
                        <option value="tx">Texas</option>
                        <option value="ut">Utah</option>
                        <option value="vt">Vermont</option>
                        <option value="va">Virginia</option>
                        <option value="wa">Washington</option>
                        <option value="wv">West Virginia</option>
                        <option value="wi">Wisconsin</option>
                        <option value="wy">Wyoming</option>
                      </select>
                    </div>
                  </div>

                  {/* Plot number — optional */}
                  <div className="auth-field">
                    <label>Plot Number <span className="optional">(optional)</span></label>
                    <input
                      type="text"
                      placeholder="e.g. Section 4, Lot 12, Space 3"
                      maxLength={100}
                      value={newGravesite.plotNumber}
                      onChange={(e) => setNewGravesite(prev => ({ ...prev, plotNumber: e.target.value }))}
                    />
                  </div>

                  {/* Notes — optional */}
                  <div className="auth-field">
                    <label>Notes <span className="optional">(optional)</span></label>
                    <textarea
                      className="booking-textarea"
                      placeholder="e.g. Prefers white flowers. Near the oak tree by the east entrance."
                      value={newGravesite.notes}
                      onChange={(e) => setNewGravesite(prev => ({ ...prev, notes: e.target.value }))}
                      rows={3}
                    />
                  </div>

                  <button
                    className="btn-primary btn-full"
                    onClick={handleSaveGravesite}
                    disabled={savingGravesite}
                  >
                    {savingGravesite ? 'Saving...' : 'Save Gravesite'}
                  </button>
                </div>
              )}

              {/* Saved gravesites list */}
              {gravesites.length > 0 ? (
                <div className="gravesites-list">
                  {gravesites.map(gravesite => (
                    <div key={gravesite.id} className="gravesite-card">
                      <div className="gravesite-card-left">
                        <div className="gravesite-icon">🪦</div>
                        <div className="gravesite-info">
                          <h4>{gravesite.deceasedName || gravesite.lovedOneName}</h4>
                          <p>{capitalize(gravesite.cemeteryName)}</p>
                          <p>{capitalize(gravesite.cemeteryCity)}, {gravesite.cemeteryState?.toUpperCase()}</p>
                          {gravesite.plotNumber && (
                            <p className="gravesite-plot">Plot: {gravesite.plotNumber}</p>
                          )}
                          {gravesite.notes && (
                            <p className="gravesite-notes">📝 {gravesite.notes}</p>
                          )}
                        </div>
                      </div>
                      <div className="gravesite-card-right">
                        {/* Book a visit directly from this gravesite */}
                        <button
                          className="btn-primary"
                          style={{ fontSize: '13px', padding: '8px 16px' }}
                          onClick={() => navigate('/book', { state: { gravesite } })}
                        >
                          Book Visit
                        </button>
                        {/* Delete gravesite */}
                        <button
                          className="btn-delete-gravesite"
                          onClick={() => handleDeleteGravesite(gravesite.id)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="gravesites-empty">
                  <div className="gravesites-empty-icon">🪦</div>
                  <p>No gravesites saved yet</p>
                  <p className="gravesites-empty-sub">
                    Add a gravesite to make booking faster next time
                  </p>
                </div>
              )}
            </div>

          </div>
        )}

      </div>

      {/* Rating modal */}
      {ratingBooking && (
        <RatingModal
          booking={ratingBooking}
          onClose={() => setRatingBooking(null)}
          onRatingSubmitted={handleRatingSubmitted}
        />
      )}

      {/* Change password modal */}
      {showChangePw && (
        <div className="modal-overlay" onClick={() => { setShowChangePw(false); setChangePwError(''); setChangePwSuccess(false); }}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowChangePw(false)}>×</button>
            <h3 className="modal-title">Change Password</h3>
            {changePwSuccess ? (
              <p style={{ color: '#4a7c59', textAlign: 'center', padding: '16px 0' }}>Password updated successfully.</p>
            ) : (
              <>
                <input
                  className="auth-input"
                  type="password"
                  placeholder="Current password"
                  value={changePwForm.current}
                  onChange={e => setChangePwForm(f => ({ ...f, current: e.target.value }))}
                  style={{ marginBottom: '10px' }}
                />
                <input
                  className="auth-input"
                  type="password"
                  placeholder="New password"
                  value={changePwForm.next}
                  onChange={e => setChangePwForm(f => ({ ...f, next: e.target.value }))}
                  style={{ marginBottom: '6px' }}
                />
                {changePwForm.next && (
                  <div className="pw-strength" style={{ marginBottom: '10px' }}>
                    {[
                      { key: 'length', label: 'At least 8 characters' },
                      { key: 'upper', label: 'One uppercase letter' },
                      { key: 'number', label: 'One number' },
                      { key: 'special', label: 'One special character' },
                    ].map(({ key, label }) => {
                      const pass = pwChecks(changePwForm.next)[key];
                      return (
                        <div key={key} className={`pw-check ${pass ? 'pw-check-pass' : 'pw-check-fail'}`}>
                          <span>{pass ? '✓' : '✗'}</span> {label}
                        </div>
                      );
                    })}
                  </div>
                )}
                <input
                  className="auth-input"
                  type="password"
                  placeholder="Confirm new password"
                  value={changePwForm.confirm}
                  onChange={e => setChangePwForm(f => ({ ...f, confirm: e.target.value }))}
                  style={{ marginBottom: '10px' }}
                />
                {changePwError && <p className="auth-error" style={{ marginBottom: '10px' }}>{changePwError}</p>}
                <button
                  className="btn-primary"
                  style={{ width: '100%' }}
                  onClick={handleChangePassword}
                  disabled={changePwLoading}
                >
                  {changePwLoading ? 'Updating…' : 'Update Password'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ========== SUBSCRIPTION UPSELL POPUP ========== */}
      {showUpsell && (
        <div className="upsell-overlay" onClick={dismissUpsell}>
          <div className="upsell-card" onClick={e => e.stopPropagation()}>

            <h2 className="upsell-heading">Love knowing their site is cared for? 💚</h2>
            <p className="upsell-subtext">
              Never miss a visit. Choose a monthly plan and we'll handle everything
              automatically — photos, cleaning, and care every single month.
            </p>

            <div className="upsell-plans">

              <div className="upsell-plan-card">
                <h3 className="upsell-plan-name">Remembrance</h3>
                <p className="upsell-plan-price">$39<span>/month</span></p>
                <p className="upsell-plan-desc">
                  1 Memory Visit/month. Debris removal, flag placement, and photos delivered monthly.
                </p>
                <button
                  className="upsell-btn upsell-btn-outline"
                  onClick={() => { dismissUpsell(); }}
                >
                  Choose Remembrance
                </button>
              </div>

              <div className="upsell-plan-card upsell-plan-featured">
                <span className="upsell-featured-badge">Most Popular</span>
                <h3 className="upsell-plan-name">Guardian</h3>
                <p className="upsell-plan-price">$64<span>/month</span></p>
                <p className="upsell-plan-desc">
                  1 Care Visit/month. Full stone cleaning, debris removal, and monthly photos.
                </p>
                <button
                  className="upsell-btn upsell-btn-primary"
                  onClick={() => { dismissUpsell(); }}
                >
                  Choose Guardian
                </button>
              </div>

              <div className="upsell-plan-card">
                <h3 className="upsell-plan-name">Legacy</h3>
                <p className="upsell-plan-price">$99<span>/month</span></p>
                <p className="upsell-plan-desc">
                  1 Tribute Visit/month. Full cleaning, fresh flowers, and monthly photos.
                </p>
                <button
                  className="upsell-btn upsell-btn-outline"
                  onClick={() => { dismissUpsell(); }}
                >
                  Choose Legacy
                </button>
              </div>

            </div>

            <p className="upsell-fine-print">Cancel anytime. No contracts. First visit scheduled within 7 days.</p>
            <button className="upsell-dismiss" onClick={dismissUpsell}>
              No thanks, I'll stick with one-time visits
            </button>

          </div>
        </div>
      )}

      {/* Cancel booking confirmation modal */}
      {cancellingBooking && (
        <div className="modal-overlay" onClick={() => !cancelLoading && setCancellingBooking(null)}>
          <div className="modal-card cancel-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Cancel this booking?</h3>
            <p className="cancel-modal-detail">
              {cancellingBooking.lovedOneName} — {capitalize(cancellingBooking.cemeteryName)}
            </p>
            <p className="cancel-modal-sub">
              {cancellingBooking.refunded
                ? 'A full refund will be issued. Please allow 5–10 business days.'
                : 'A full refund will be issued to your original payment method.'}
            </p>
            <div className="cancel-modal-buttons">
              {/* Pass id and status explicitly — avoids stale closure on cancellingBooking */}
              <button
                className="btn-primary btn-danger"
                onClick={() => handleConfirmCancel(cancellingBooking.id, cancellingBooking.status)}
                disabled={cancelLoading}
              >
                {cancelLoading ? 'Processing refund…' : 'Yes, Cancel & Refund'}
              </button>
              <button
                className="btn-outline"
                onClick={() => setCancellingBooking(null)}
                disabled={cancelLoading}
              >
                Keep Booking
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancellation success banner — auto-clears after 8 seconds */}
      {cancelSuccess && (
        <div
          className="photo-ready-banner"
          style={{ background: '#d1e7dd', color: '#0a3622', cursor: 'default' }}
          onClick={() => setCancelSuccess('')}
        >
          <span className="photo-ready-icon">✅</span>
          <span>{cancelSuccess}</span>
        </div>
      )}

      {/* Cancellation error banner */}
      {cancelError && (
        <div
          className="photo-ready-banner"
          style={{ background: '#f8d7da', color: '#842029', cursor: 'default' }}
          onClick={() => setCancelError('')}
        >
          <span className="photo-ready-icon">⚠️</span>
          <span>{cancelError}</span>
        </div>
      )}

    </div>
  );
}

export default Dashboard;